export type ImportDomain = 'PRODUCTS' | 'SUPPLIERS' | 'LOCATIONS' | 'INITIAL_INVENTORY';
export type ImportJobStatus =
  | 'UPLOADED'
  | 'MAPPED'
  | 'VALIDATING'
  | 'VALIDATED'
  | 'VALIDATION_FAILED'
  | 'EXECUTING'
  | 'COMPLETED'
  | 'EXECUTION_FAILED'
  | 'CANCELLED';
export type ImportRowStatus = 'PENDING' | 'VALID' | 'ERROR' | 'IMPORTED' | 'SKIPPED';

export interface ImportJobListItem {
  readonly id: string;
  readonly docNo: string;
  readonly domain: ImportDomain;
  readonly status: ImportJobStatus;
  readonly originalFilename: string;
  readonly rowCount: number;
  readonly validCount: number;
  readonly errorCount: number;
  readonly importedCount: number;
  readonly skippedCount: number;
  readonly createdAt: string;
}

export interface ImportJobDetail extends ImportJobListItem {
  readonly fieldMapping: Record<string, string> | null;
  readonly failureReason: string | null;
}

export interface ImportRowError {
  readonly field?: string;
  readonly code: string;
  readonly params?: Record<string, unknown>;
}

export interface ImportJobRowItem {
  readonly id: string;
  readonly rowNumber: number;
  readonly rawData: Record<string, unknown>;
  readonly mappedData: Record<string, unknown> | null;
  readonly status: ImportRowStatus;
  readonly errors: readonly ImportRowError[] | null;
  readonly entityId: string | null;
}

export interface DomainFieldSpec {
  readonly key: string;
  readonly required: boolean;
  readonly aliases: readonly string[];
}

export const DOMAIN_FIELDS: Record<ImportDomain, readonly DomainFieldSpec[]> = {
  PRODUCTS: [
    { key: 'sku', required: true, aliases: ['sku', 'productcode'] },
    { key: 'name', required: true, aliases: ['name', 'productname', 'title'] },
    { key: 'baseUnitCode', required: true, aliases: ['baseunit', 'unit', 'baseunitcode'] },
    { key: 'brandName', required: false, aliases: ['brand'] },
    { key: 'categoryName', required: false, aliases: ['category'] },
    { key: 'originName', required: false, aliases: ['origin'] },
    { key: 'defaultSupplierCode', required: false, aliases: ['suppliercode', 'defaultsupplier'] },
    { key: 'description', required: false, aliases: [] },
    { key: 'notes', required: false, aliases: [] },
    { key: 'tracksExpiration', required: false, aliases: ['trackexpiration'] },
    { key: 'defaultShelfLifeDays', required: false, aliases: ['shelflifedays'] },
    { key: 'shelfLifeBasis', required: false, aliases: [] },
  ],
  SUPPLIERS: [
    { key: 'code', required: true, aliases: ['suppliercode'] },
    { key: 'name', required: true, aliases: ['suppliername'] },
    { key: 'contactPerson', required: false, aliases: ['contact'] },
    { key: 'phone', required: false, aliases: [] },
    { key: 'whatsapp', required: false, aliases: [] },
    { key: 'email', required: false, aliases: [] },
    { key: 'address', required: false, aliases: [] },
    { key: 'countryCode', required: false, aliases: ['country'] },
    { key: 'taxId', required: false, aliases: ['tax'] },
    { key: 'notes', required: false, aliases: [] },
  ],
  LOCATIONS: [
    { key: 'code', required: true, aliases: ['locationcode'] },
    { key: 'name', required: true, aliases: ['locationname'] },
    { key: 'parentCode', required: false, aliases: ['parent', 'parentlocationcode'] },
    { key: 'typeCode', required: false, aliases: ['type', 'locationtype'] },
    { key: 'canStoreInventory', required: false, aliases: ['canstore', 'storage'] },
    { key: 'sortOrder', required: false, aliases: ['order'] },
  ],
  INITIAL_INVENTORY: [
    { key: 'productSku', required: true, aliases: ['sku'] },
    { key: 'locationCode', required: true, aliases: ['location'] },
    { key: 'quantity', required: true, aliases: ['qty'] },
    { key: 'unitCode', required: true, aliases: ['unit'] },
    { key: 'expirationDate', required: false, aliases: ['expiry', 'expdate'] },
    { key: 'noExpiration', required: false, aliases: [] },
    { key: 'externalLotNo', required: false, aliases: ['lotno', 'batchno'] },
    { key: 'supplierCode', required: false, aliases: [] },
    { key: 'unitCost', required: false, aliases: ['cost'] },
    { key: 'currency', required: false, aliases: [] },
  ],
};

export type ExportDataset =
  | 'CURRENT_INVENTORY'
  | 'EXPIRING'
  | 'EXPIRED'
  | 'MOVEMENTS'
  | 'PRODUCTS'
  | 'SUPPLIERS'
  | 'COUNT_VARIANCES'
  | 'OVERRIDE_AUDIT';

export const EXPORT_DATASETS: readonly ExportDataset[] = [
  'CURRENT_INVENTORY',
  'EXPIRING',
  'EXPIRED',
  'MOVEMENTS',
  'PRODUCTS',
  'SUPPLIERS',
  'COUNT_VARIANCES',
  'OVERRIDE_AUDIT',
];

export interface ExportJobListItem {
  readonly id: string;
  readonly dataset: ExportDataset;
  readonly format: 'XLSX' | 'CSV';
  readonly status: 'QUEUED' | 'RUNNING' | 'DONE' | 'FAILED';
  readonly rowCount: number | null;
  readonly expiresAt: string | null;
  readonly createdAt: string;
}
